import React, { useState, useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { FiPieChart } from "react-icons/fi";
import { COLORS, SOURCE_COLORS, TABLE_RANGES } from "../myDashboardConstants";
import { formatIndianCurrency } from "../utils";
import { SectionCard } from "../components/SectionCards";
import { RangeTabs } from "../components/RangeTabs";
import { ChartTooltip } from "../components/ChartTooltip";
export const WIN_RATE = { value: 62, prevValue: 57 };
export function LeadSources({ data, range, onRangeChange }) {
  const [hoverIdx, setHoverIdx] = useState(null);

  const chartData = useMemo(() => {
    const rows = Array.isArray(data?.top_leads) ? data?.top_leads : [];
    const total = rows.reduce(
      (sum, r) => sum + (Number(r.total_budget) || 0),
      0,
    );
    return rows.map((r) => ({
      name: r.source_title,
      rawValue: Number(r.total_budget) || 0,
      value:
        total > 0
          ? Number(((Number(r.total_budget) / total) * 100).toFixed(1))
          : 0,
    }));
  }, [data]);
  const totalLeads = useMemo(
    () => chartData.reduce((sum, r) => sum + r.rawValue, 0),
    [chartData],
  );
  const hasData = chartData.length > 0 && totalLeads > 0;
  // faint placeholder ring — single full segment, muted gray, still hoverable
  const displayData = hasData
    ? chartData
    : [{ name: "No data", rawValue: 0, value: 100 }];
  return (
    <SectionCard
      title="Sales by Lead Source"
      subtitle="Share of closed deals"
      icon={<FiPieChart size={16} />}
      accent={COLORS.success}
      action={
        <RangeTabs
          value={range}
          onChange={onRangeChange}
          options={TABLE_RANGES}
        />
      }
    >
      <div className="sd-pie-wrap">
        <div style={{ width: "100%", height: 220, position: "relative" }}>
          <ResponsiveContainer>
            <PieChart>
              <Tooltip
                content={
                  <ChartTooltip
                    formatter={(v, n, p) =>
                      formatIndianCurrency(p?.payload?.rawValue ?? 0)
                    }
                  />
                }
                isAnimationActive={false}
                wrapperStyle={{ pointerEvents: "none", zIndex: 50 }}
              />
              <Pie
                data={displayData}
                dataKey="value"
                nameKey="name"
                innerRadius={55}
                outerRadius={85}
                paddingAngle={hasData ? 3 : 0}
                cornerRadius={hasData ? 6 : 0}
                isAnimationActive={false}
                onMouseEnter={(_, i) => hasData && setHoverIdx(i)}
                onMouseLeave={() => setHoverIdx(null)}
              >
                {displayData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={
                      hasData
                        ? SOURCE_COLORS[i % SOURCE_COLORS.length]
                        : "#E2E7F0"
                    }
                    stroke="none"
                    fillOpacity={hasData ? 1 : 0.55}
                    opacity={
                      !hasData || hoverIdx === null || hoverIdx === i ? 1 : 0.35
                    }
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <div className="sd-radial-center">
            <div
              className="sd-radial-pct"
              style={{
                fontSize: 20,
                lineHeight: 1.2,
                color: hasData ? undefined : COLORS.muted,
              }}
            >
              {formatIndianCurrency(totalLeads)}
            </div>
            <div className="sd-radial-caption">Total Budget</div>
          </div>
        </div>
        <div className="sd-pie-legend">
          {(hasData ? chartData : []).map((s, i) => (
            <div
              key={s.name + i}
              className="sd-pie-legend-row"
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
              style={{
                opacity: hoverIdx === null || hoverIdx === i ? 1 : 0.45,
                cursor: "pointer",
              }}
            >
              <span
                className="sd-pie-dot"
                style={{ background: SOURCE_COLORS[i % SOURCE_COLORS.length] }}
              />
              <span className="sd-pie-legend-name" title={s.name}>
                {s.name}
              </span>
              <span className="sd-pie-legend-value">
                {formatIndianCurrency(s.rawValue)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </SectionCard>
  );
}
